import { ClientPostTypeEnum } from '@DigitaleDoerfer/digitale-doerfer-api';

import { MAX_OPTION_COUNT } from '@/features/post-editor/constants/poll';
import { PostEditorRoute } from '@/features/post-editor/navigation/postEditorRoute';
import { PostFeedType } from '@/features/posts/types/postFeed';

export const postEditorGermanLocalization = {
  routes: {
    [PostEditorRoute.PostEditor]: 'Bearbeiten'
  },
  components: {
    poll: {
      card: {
        singleSelect: 'Eine Antwort möglich.',
        multiselect: 'Mehrere Antworten möglich.',
        endsAt: 'Endet am {{date}} um {{time}} Uhr.',
        endedAt:
          'Beendet am {{date}} um {{time}} Uhr. Teilnahme nicht mehr möglich.'
      },
      preview: {
        edit: 'Bearbeiten',
        note: 'Hinweis: Nach der Veröffentlichung kann die Umfrage nicht mehr bearbeitet werden.',
        alerts: {
          delete: {
            title: 'Umfrage löschen?',
            content:
              'Alle bisherigen Antworten gehen dabei unwiderruflich verloren.',
            confirm: 'Löschen',
            cancel: 'Abbrechen'
          },
          closeUnsaved: {
            title: 'Formular verlassen?',
            content:
              'Es sind noch nicht gespeicherte Angaben vorhanden. Die Daten gehen verloren, wenn du ohne zu speichern fortfährst.',
            cancel: 'Abbrechen',
            confirm: 'Ohne Speichern fortfahren'
          }
        }
      },
      form: {
        poll: 'Umfrage',
        allowMultiselect: 'Mehrfachauswahl erlauben',
        question: {
          title: 'Deine Frage',
          error: {
            empty: 'Bitte füge eine Frage hinzu.',
            max: 'Die Frage darf nicht mehr als {{maxChar}} Zeichen enthalten.'
          }
        },
        options: `Antworten ({{count}}/${MAX_OPTION_COUNT})`,
        option: {
          title: 'Antwort {{index}}',
          error: {
            empty: 'Die Antwort darf nicht leer sein.',
            unique: 'Die Antwort darf sich nicht wiederholen.',
            max: 'Die Antwort darf nicht mehr als {{maxChar}} Zeichen enthalten.'
          }
        },
        notUnique: 'Bitte füge mindestens zwei verschiedene Antworten hinzu.',
        addOption: 'Antwort hinzufügen',
        endsAt: 'Endet am',
        date: {
          title: 'Datum',
          placeholder: 'DD.MM.JJJJ',
          error: {
            past: 'Das Ende der Umfrage darf nicht in der Vergangenheit liegen.',
            future:
              'Das Ende der Umfrage darf nicht zu weit in der Zukunft liegen. Enddatum muss vor {{dateTime}} liegen.'
          }
        },
        time: { title: 'Uhrzeit', placeholder: 'HH:MM' }
      }
    }
  },
  screens: {
    'post-editor': {
      onlyIn: 'Nur für {{name}}',
      yourHomeGeoArea: 'deine Heimatgemeinde',
      date: 'Datum',
      location: 'Ort',
      category: 'Kategorie',
      selectCategory: 'Kategorie auswählen',
      public: 'Öffentlich',
      myGroups: 'Meine Gruppen',
      create: 'Erstellen',
      edit: 'Bearbeiten',
      send: 'Funken',
      allDay: 'Ganztags',
      start: 'Startdatum',
      end: 'Enddatum',
      time: 'Uhrzeit',
      happeningStart: 'Start',
      happeningEnd: 'Ende',

      locationPicker: {
        currentLocation: 'Standort',
        changePosition: 'Position ändern',
        searchButton: 'Nach Ort suchen',
        locationNameLabel: 'Eigene Ortsbezeichnung (optional)',
        locationNamePlaceholder: 'z. B. „Waldlichtung Nord“',
        locationNameAccessibilityLabel: 'Eigene Ortsbezeichnung (optional)',
        locationSearchPlaceholder: 'Nach Ort suchen',
        locationSearchCurrentLocation: 'Aktueller Standort',
        state: {
          loading: { title: 'Orte werden geladen' },
          error: {
            title: 'Orte konnten nicht geladen werden',
            subtitle:
              'Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.'
          }
        }
      },
      placeholders: {
        [PostFeedType.GOSSIP]:
          'Von kurzen Fragen bis zu Gesprächen mit ordentlich Tiefgang…',
        [PostFeedType.HAPPENING]:
          'Du planst die Party des Jahres oder den nächsten Flohmarkt? Mach deine Nachbarschaft darauf aufmerksam!',
        [ClientPostTypeEnum.Offer]:
          'Biete eine Mitfahrgelegenheit, Werkzeug oder gleich dein handwerkliches Geschick an…',
        [ClientPostTypeEnum.Seeking]:
          'Suche eine Mitfahrgelegenheit, ein Werkzeug oder Hilfe für dein Vorhaben…',
        [PostFeedType.SPECIAL_POST]:
          'Hier kannst du deiner Umgebung Hilfe anbieten oder selbst nach Unterstützung fragen.',
        [PostFeedType.SUGGESTION]:
          'Teile deinen Vorschlag, Wunsch oder Mangel mit der Gemeindeverwaltung…',
        [PostFeedType.EMERGENCY]: '',
        [PostFeedType.NEWS_ITEM]: '',
        [PostFeedType.CIVIL_PROTECTION]: '',
        [PostFeedType.ALL]: ''
      },
      alerts: {
        documentSize: {
          title: 'Datei zu groß',
          content: 'Die Datei darf maximal {{maxSizeKb}} KB groß sein.',
          confirm: 'OK'
        }
      },
      state: {
        loading: { title: 'Funk wird geladen' },
        error: {
          title: 'Funk konnte nicht geladen werden',
          subtitle: 'Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.'
        }
      },
      menu: {
        selectImage: 'Aus Bibliothek auswählen',
        takeImage: 'Foto aufnehmen'
      },
      accessibility: {
        editorHint: 'Beitragstext eingeben',
        editorLabel: 'Beitragstext'
      },
      cancel: 'Abbrechen'
    }
  }
};

// Todo: Define exact type
// Guarantees that both translations have the same structure
type PostsLocalization = typeof postEditorGermanLocalization;

export const postEditorEnglishLocalization: PostsLocalization = {
  routes: {
    [PostEditorRoute.PostEditor]: 'Edit'
  },
  components: {
    poll: {
      card: {
        singleSelect: 'Single answers possible.',
        multiselect: 'Multiple answers possible.',
        endsAt: 'Ends on {{date}} at {{time}}.',
        endedAt:
          'Ends at {{date}} um {{time}}. Participation is no longer possible.'
      },
      preview: {
        edit: 'Edit',
        note: 'Note: The poll cannot be edited after publication.',
        alerts: {
          delete: {
            title: 'Delete Poll',
            content: 'All previous answers will be permanently lost.',
            confirm: 'Delete',
            cancel: 'Cancel'
          },
          closeUnsaved: {
            title: 'Leave form?',
            content:
              'There are unsaved entries. The data will be lost if you proceed without saving.',
            cancel: 'Cancel',
            confirm: 'Continue without saving'
          }
        }
      },
      form: {
        poll: 'Poll',
        allowMultiselect: 'Allow multiple selection',
        question: {
          title: 'Your question',
          error: {
            empty: 'Please add a question.',
            max: 'The question should not contain more than {{maxChar}} characters.'
          }
        },
        options: `Answers ({{count}}/${MAX_OPTION_COUNT})`,
        option: {
          title: 'Answer {{index}}',
          error: {
            empty: 'The answer must not be empty.',
            unique: 'The answer must not be repeated.',
            max: 'The answer should not contain more than  {{maxChar}} characters.'
          }
        },
        notUnique: 'Please add at least two different answers.',
        addOption: 'Add answer',
        endsAt: 'Ends on',
        date: {
          title: 'Date',
          placeholder: 'MM/DD/YYYY',
          error: {
            past: 'The end of the survey must not be in the past.',
            future:
              'The end of the survey must not be to far in the future. End date must be before {{dateTime}}.'
          }
        },
        time: { title: 'Time', placeholder: 'HH:MM' }
      }
    }
  },
  screens: {
    'post-editor': {
      onlyIn: 'Only in {{name}}',
      yourHomeGeoArea: 'your home community',
      date: 'Date',
      location: 'Location',
      category: 'Kategorie',
      selectCategory: 'Select category',
      public: 'Public',
      myGroups: 'My Groups',
      create: 'Create',
      edit: 'Edit',
      send: 'Transmit',
      allDay: 'Full day',
      start: 'Start',
      end: 'End',
      time: 'Time',
      happeningStart: 'Start of the event',
      happeningEnd: 'End of the event',

      locationPicker: {
        currentLocation: 'Location',
        changePosition: 'Move position',
        searchButton: 'Search location',
        locationNameLabel: 'Custom location name (optional)',
        locationNamePlaceholder: 'e.g., „northern forest clearing“',
        locationNameAccessibilityLabel: 'Custom location name (optional)',
        locationSearchPlaceholder: 'Search for location',
        locationSearchCurrentLocation: 'Current location',
        state: {
          loading: { title: 'Locations are loading' },
          error: {
            title: 'Locations could not be loaded',
            subtitle: 'Please try again later.'
          }
        }
      },
      placeholders: {
        [PostFeedType.GOSSIP]:
          'From brief questions to conversations with substantial depth…',
        [PostFeedType.HAPPENING]:
          'Are you planning the party of the year or the next flea market? Make your neighborhood aware!',
        [ClientPostTypeEnum.Offer]:
          'Offer a ride, a tool, or your craftsmanship skills…',
        [ClientPostTypeEnum.Seeking]:
          'Looking for a ride, a tool, or help with your project…',
        [PostFeedType.SPECIAL_POST]:
          'Here you can offer help to your community or ask for support yourself.',
        [PostFeedType.SUGGESTION]:
          'Share your suggestion, request, or deficiency with the municipal administration…',
        [PostFeedType.EMERGENCY]: '',
        [PostFeedType.NEWS_ITEM]: '',
        [PostFeedType.CIVIL_PROTECTION]: '',
        [PostFeedType.ALL]: ''
      },
      alerts: {
        documentSize: {
          title: 'File too big',
          content: 'File size must not exceed {{maxSizeKb}} KB.',
          confirm: 'OK'
        }
      },
      state: {
        loading: { title: 'Post is loading' },
        error: {
          title: 'Post could not be loaded',
          subtitle: 'Please try again later.'
        }
      },
      menu: {
        selectImage: 'Select from library',
        takeImage: 'Take a photo'
      },
      accessibility: {
        editorHint: 'Enter post text',
        editorLabel: 'Post text'
      },
      cancel: 'Cancel'
    }
  }
};
